import {DatePipe} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';
import {debounceTime, Subject, take, takeUntil, forkJoin} from 'rxjs';
import {ReferenceLookupValue} from 'src/app/modules/clm/types/reference-lookup-value.enum';
import {detailsExpand} from 'src/app/modules/common';
import {DisplayStrings} from 'src/app/modules/common/constants/DisplayStrings';
import {ConfirmationDialogComponent} from 'src/app/modules/common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {ErrorDialogComponent} from 'src/app/modules/common/dialogs/error-dialog/error-dialog.component';
import {PagedRequestDTO} from 'src/app/modules/common/models/PagedRequestDTO';
import {PagedResponseDTO} from 'src/app/modules/common/models/PagedResponseDTO';
import {SharedService} from 'src/app/modules/common/services/shared-service';
import {DateUtil} from 'src/app/modules/common/util/DateUtil';
import {Common} from 'src/app/modules/common/util/Util';
import {PolicyholderGroupDTO} from 'src/app/modules/ent/models/PolicyholderGroupDTO';
import {PolicyholderGroupService} from 'src/app/modules/ent/services/policyholder-group.service';
import {ApplicationRowDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ApplicationRowDTO';
import {BulkApplicationDTO} from 'src/app/modules/onboarding-admin-portal/models/application/BulkApplicationDTO';
import {ReferenceDTO} from 'src/app/modules/onboarding-admin-portal/models/application/ReferenceDTO';
import {BrokerDTO} from 'src/app/modules/onboarding-admin-portal/models/broker/BrokerDTO';
import {BrokerService} from 'src/app/modules/onboarding-admin-portal/services/broker.service';
import {OnboardingAdminPortalService} from 'src/app/modules/onboarding-admin-portal/services/onboard-application.service';
import {ReferenceService} from 'src/app/modules/onboarding-admin-portal/services/reference.service';
import {OtherRequestBuilder} from 'src/app/modules/other/services/other-request-builder';
import * as XLSX from 'xlsx';
import {FileUploadService} from '../../../../common/services/file-upload.service';
import {PortalType, buildRoute, PathSegments} from '../../../../common/types/portal.enum';
import {EnterpriseRoutesQueryParam} from '../../../../common/types/routes-query-param.enum';
import {ApplicationImportValidationUtil, GlobalValidationError} from '../../utils/ApplicationImportValidationUtil';
import {ApplicationUtil} from '../../utils/ApplicationUtil';
import {ObdBulkApplicationRowEditComponent} from '../obd-bulk-application-row-edit/obd-bulk-application-row-edit.component';

export interface StartDateOption {
  value: string;
  displayValue: string;
  isCurrentMonth: boolean;
}

enum TableColumns {
  Actions = 'actions',
  AppRefNumber = 'applicationReferenceNumber',
  Relationship = 'relationship',
  Title = 'title',
  Name = 'name',
  Surname = 'surname',
  IdType = 'idType',
  IdNumber = 'idNumber',
  DateOfBirth = 'dob',
  Gender = 'gender',
  MaritalStatus = 'maritalStatus',
  Ethnicity = 'ethnicity',
  Option = 'option'
}

enum ApplicationRowIndex {
  ApplicationReferenceNumber = 0,
  Relationship = 1,
  Title = 2,
  Name = 3,
  Surname = 4,
  IdType = 5,
  IdNumber = 6,
  Dob = 7,
  Gender = 8,
  MaritalStatus = 9,
  Ethnicity = 10,
  ContactNumber = 11,
  EmailAddress = 12,
  BankName = 13,
  AccountNumber = 14,
  AccountType = 15,
  AddressType = 16,
  AddressNumber = 17,
  AddressName = 18,
  AddressSuburb = 19,
  EmployeeNumber = 20,
  EmploymentDate = 21,
  Option = 22,
  IncomeAmount = 23
}

enum Controls {
  Broker = 'broker',
  EmployerGroup = 'employerGroup',
  MembershipStartDate = 'membershipStartDate'
}

enum ApplicationFilters {
  All = 'All Applications',
  Invalid = 'Invalid',
  Valid = 'Valid'
}

export interface ApplicationRowReferenceData {
  relationshipTypes?: ReferenceDTO[];
  titles?: ReferenceDTO[];
  idTypes?: ReferenceDTO[];
  genders?: ReferenceDTO[];
  maritalStatuses?: ReferenceDTO[];
  ethnicities?: ReferenceDTO[];
  bankOptions?: ReferenceDTO[];
  accountTypes?: ReferenceDTO[];
  addressTypes?: ReferenceDTO[];
  options?: ReferenceDTO[];
  directPayingMemberGroups?: ReferenceDTO[];
}

@Component({
  selector: 'greydom-obd-bulk-application-import',
  templateUrl: './obd-bulk-application-import.component.html',
  styleUrls: ['obd-bulk-application-import.component.scss'],
  animations: [detailsExpand],
  providers: [DatePipe]
})
export class ObdBulkApplicationImportComponent implements OnInit, OnDestroy {
  readonly APPLICATION_BULK_UPLOAD_FILE_ID = 1000003; // Fix FileID in DB
  readonly FILE_EXTENSIONS = [Common.MIME_TYPE_XLSX, Common.MIME_TYPE_XLS, Common.MIME_TYPE_XLSM];
  readonly displayStrings = DisplayStrings;
  readonly BUSINESS_TYPE_ATOMIC_GROUP_ID = 17;

  readonly dateFormat: string = DateUtil.dateFormat;
  readonly debounceTime = Common.HALF_SECOND_DEBOUNCE;
  readonly destroyed$ = new Subject<boolean>();
  readonly tableColumns = TableColumns;
  readonly Controls = Controls;
  readonly DisplayStrings = DisplayStrings;

  applicationFilterOptions: string[] = Object.values(ApplicationFilters);
  displayedColumns: string[] = Object.values(TableColumns);
  tabName: string;
  customRoute: string;
  currentPortal: PortalType;
  loggedInUserName: string;
  heading = 'Import Applications';
  backPageName = 'enterprise-management';
  bulkApplications: BulkApplicationDTO;
  expandedElement: ApplicationRowDTO | null = null;
  generalDetailsForm: FormGroup;

  // Reference data for the edit dialog
  referenceData: ApplicationRowReferenceData = {};

  selectedBroker: BrokerDTO;
  filteredBrokers: BrokerDTO[] = [];
  selectedEmployerGroup: PolicyholderGroupDTO;
  filteredEmployerGroups: PolicyholderGroupDTO[] = [];
  brokerTextChanged: Subject<string> = new Subject<string>();
  employerGroupTextChanged: Subject<string> = new Subject<string>();
  paginatorControls = OtherRequestBuilder.getTableControls();
  applicationFilter = ApplicationFilters.All;
  dataSource: MatTableDataSource<ApplicationRowDTO>;
  startDateOptions: StartDateOption[] = [];
  private filteredBrokers$ = new Subject<BrokerDTO[]>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private fileService: FileUploadService,
    private brokerService: BrokerService,
    private policyholderGroupService: PolicyholderGroupService,
    private sharedService: SharedService,
    private formBuilder: FormBuilder,
    private keycloakService: KeycloakService,
    private dialog: MatDialog,
    private referenceService: ReferenceService,
    private onboardingService: OnboardingAdminPortalService,
  ) {
    const parentData = this.activatedRoute.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
    this.customRoute = this.getCustomRoute();
    this.tabName = EnterpriseRoutesQueryParam.Applications;
  }

  @ViewChild('MatSortBulApplication') set matSort(ms: MatSort) {
    if (!ms) {
      return;
    }
    this.dataSource.sort = ms;
  }

  @ViewChild('MatPaginatorBulkApplication') set matPaginator(mp: MatPaginator) {
    if (!mp) {
      return;
    }
    this.dataSource.paginator = mp;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initSearchTextChangedSubscriptions();
    this.initStartDateSubscription();
    this.loadReferenceData();
    this.handlePortalBasedCustomizations(this.currentPortal);
    this.getLoggedInUserName();
    this.generateStartDateOptions();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initializeForm(): void {
    this.generalDetailsForm = this.formBuilder.group({
      [Controls.EmployerGroup]: ['', [Validators.required]],
      [Controls.Broker]: ['', [Validators.required]],
      [Controls.MembershipStartDate]: [null, [Validators.required]]
    });
  }

  initSearchTextChangedSubscriptions(): void {
    this.employerGroupTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        if (value) {
          this.searchEmployerGroups(value);
        }
      });

    this.brokerTextChanged
      .pipe(
        debounceTime(this.debounceTime),
        takeUntil(this.destroyed$)
      )
      .subscribe(value => {
        if (value) {
          this.searchBrokers(value);
        }
      });
  }

  initStartDateSubscription(): void {
    this.generalDetailsForm.get(Controls.MembershipStartDate)?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(date => {
        if (date) {
          this.updateAllApplicationsStartDate(date);
        }
      });
  }

  onTemplateFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length !== 1) {
      this.openErrorDialog(DisplayStrings.bulkApplicationErrorMessagesList.excelMultipleFileError);
      return;
    }
    try {
      const file = input.files[0];
      if (!this.FILE_EXTENSIONS.includes(file.type)) {
        this.openErrorDialog(DisplayStrings.bulkApplicationErrorMessagesList.notExcelFileError);
        return;
      }
      this.bulkApplications = new BulkApplicationDTO();
      this.bulkApplications.applications = [];
      const reader: FileReader = new FileReader();

      // Filter Controls
      const brokerControl = this.generalDetailsForm.get(Controls.Broker);
      const brokerCode = this.parseDisplayString(brokerControl?.value)?.code;
      const employerGroupControl = this.generalDetailsForm.get(Controls.EmployerGroup);
      const employerGroupCode = this.parseDisplayString(employerGroupControl?.value)?.code;
      const membershipStartDate = this.generalDetailsForm.get(Controls.MembershipStartDate)?.value;

      if (!brokerCode || !employerGroupCode || !membershipStartDate) {
        return;
      }

      reader.onload = (e: any) => {
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, {type: 'binary'});

        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];

        const data: any[][] = XLSX.utils.sheet_to_json(ws, {header: 1, defval: ''});

        const headerRow = data[0];
        if (!headerRow || headerRow.length === 0 || headerRow.every(cell => cell === '')) {
          this.openErrorDialog(DisplayStrings.bulkApplicationErrorMessagesList.excelFileMissingHeaders);
          return;
        }

        const totalRows = data.length - 1; // Exclude header row
        if (totalRows == 0) {
          this.openErrorDialog(DisplayStrings.bulkApplicationErrorMessagesList.excelEmptyError);
          return;
        }

        const rawRows = data.slice(1);
        const processedRows: ApplicationRowDTO[] = rawRows
          .filter(row => this.isValidRow(row))
          .map(row => {
            return {
              applicationReferenceNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.ApplicationReferenceNumber]),
              relationship: this.sanitiseInputString(row[ApplicationRowIndex.Relationship]),
              title: this.sanitiseInputString(row[ApplicationRowIndex.Title]),
              name: this.sanitiseInputString(row[ApplicationRowIndex.Name]),
              surname: this.sanitiseInputString(row[ApplicationRowIndex.Surname]),
              idType: this.sanitiseInputString(row[ApplicationRowIndex.IdType]),
              idNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.IdNumber]),
              dob: this.safeFormatDate(this.dateFormat, row[ApplicationRowIndex.Dob]),
              gender: this.sanitiseInputString(row[ApplicationRowIndex.Gender]),
              maritalStatus: this.sanitiseInputString(row[ApplicationRowIndex.MaritalStatus]),
              ethnicity: this.sanitiseInputString(row[ApplicationRowIndex.Ethnicity]),
              contactNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.ContactNumber]),
              emailAddress: this.sanitiseEmailString(row[ApplicationRowIndex.EmailAddress]),
              bankName: this.sanitiseInputString(row[ApplicationRowIndex.BankName]),
              accountNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.AccountNumber]),
              accountType: this.sanitiseInputString(row[ApplicationRowIndex.AccountType]),
              addressType: this.sanitiseInputString(row[ApplicationRowIndex.AddressType]),
              addressNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.AddressNumber]),
              addressName: this.sanitiseInputString(row[ApplicationRowIndex.AddressName]),
              addressSuburb: this.sanitiseInputString(row[ApplicationRowIndex.AddressSuburb]),
              employeeNumber: this.sanitiseInputStringAndRemoveWhitespace(row[ApplicationRowIndex.EmployeeNumber]),
              employmentDate: this.safeFormatDate(this.dateFormat, row[ApplicationRowIndex.EmploymentDate]),
              option: this.sanitiseInputString(row[ApplicationRowIndex.Option]),
              incomeAmount: this.truncateToTwoDecimals(row[ApplicationRowIndex.IncomeAmount]),
              brokerCode,
              employerGroupCode,
              membershipStartDate,
              uploadedBy: this.loggedInUserName,
              batchReferenceNumber: this.getBatchReferenceNumber(brokerCode, employerGroupCode, this.loggedInUserName),
              validationErrors: []
            };
          });

        if (!processedRows || processedRows.length > 1000) {
          this.openErrorDialog(DisplayStrings.bulkApplicationErrorMessagesList.excelSizeTooBigError);
          return;
        }

        this.bulkApplications.applications = ApplicationImportValidationUtil.validate(processedRows, this.isDirectPayingMember(), this.referenceData);
        if (this.bulkApplications?.applications) {
          this.bulkApplications.applications = [...this.bulkApplications.applications];
          this.dataSource = new MatTableDataSource(this.bulkApplications.applications);
        }
      };

      reader.readAsBinaryString(file);
    } catch (err) {
      this.openErrorDialog(this.displayStrings.bulkApplicationErrorMessagesList.excelUploadError);
    }
  }

  onClickDownload() {
    if (!this.APPLICATION_BULK_UPLOAD_FILE_ID) {
      return;
    }
    this.fileService.downloadFile(this.APPLICATION_BULK_UPLOAD_FILE_ID, null);
  }

  revalidateAndDisplayApplicationRows() {
    if (this.bulkApplications?.applications && this.bulkApplications.applications.length > 0) {
      this.bulkApplications.applications = ApplicationImportValidationUtil.validate(this.bulkApplications.applications, this.isDirectPayingMember(), this.referenceData);
      this.bulkApplications.applications = [...this.bulkApplications.applications];
      this.applyFilter();
    }
  }

  getDisplayString(code: string | undefined, name: string | undefined): string {
    return code && name ? `${code}: ${name}` : 'N/A';
  }

  onClickOpenFileExplorer($event): void {
    if (this.generalDetailsForm?.valid) {
      if (this.bulkApplications?.applications && this.bulkApplications?.applications?.length > 0) {
        this.openFileUploadConfirmationDialog($event);
      }
      else {
        this.openFileExplorer($event);
      }
    }
  }

  openFileExplorer($event): void {
    if (this.generalDetailsForm?.valid) {
      $event.stopPropagation();
      const element = document.getElementById('upload') as HTMLInputElement;
      if (element !== null) {
        element.value = '';
        element.click();
      }
    }
  }

  onInputChangedSearchEmployerGroups($event: Event): void {
    const searchText = ($event.target as HTMLInputElement).value;
    this.employerGroupTextChanged.next(searchText);
  }

  onInputChangedSearchBrokers($event: Event): void {
    const searchText = ($event.target as HTMLInputElement).value;
    this.brokerTextChanged.next(searchText);
  }

  onBrokerSelected(selectedValue: string): void {
    const selectedBroker = this.filteredBrokers.find(
      broker => this.getDisplayString(broker.brokerNumber, broker.name) === selectedValue
    );

    if (selectedBroker) {
      this.selectedBroker = selectedBroker;
      this.setBrokerCodeAndEmployerGroupToAllData();
    }
  }

  onPolicyholderGroupSelected(selectedValue: string): void {
    const selectedGroup = this.filteredEmployerGroups.find(
      group => this.getDisplayString(group.policyholderGroupNumber, group.name) === selectedValue
    );

    if (selectedGroup) {
      this.selectedEmployerGroup = selectedGroup;
      this.setBrokerCodeAndEmployerGroupToAllData();
      this.revalidateAndDisplayApplicationRows()
    }
  }

  onBrokerBlur(): void {
    const brokerControl = this.generalDetailsForm.get(Controls.Broker);
    const currentValue = brokerControl?.value;
    const hasMatch = this.filteredBrokers.some(broker =>
      this.getDisplayString(broker.brokerNumber, broker.name)?.toLowerCase() === currentValue?.toLowerCase()
    );

    if (!currentValue || !this.filteredBrokers.length || !hasMatch) {
      brokerControl?.patchValue(null);
    }
  }

  onEmployerGroupBlur(): void {
    const employerControl = this.generalDetailsForm.get(Controls.EmployerGroup);
    const currentValue = employerControl?.value;
    const hasMatch = this.filteredEmployerGroups.some(group =>
      this.getDisplayString(group.policyholderGroupNumber, group.name)?.toLowerCase() === currentValue?.toLowerCase()
    );

    if (!currentValue || !this.filteredEmployerGroups.length || !hasMatch) {
      employerControl?.patchValue(null);
    }
  }

  searchEmployerGroups(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblPolicyholderGroup',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {}
    };

    const fields = [
      {key: 'name', name: 'name', logicGate: 'AND', bracket: '(', value: searchValue},
      {key: 'policyholderGroupNumber', name: 'policyholderGroupNumber', logicGate: 'OR', bracket: ')',value: searchValue},
      {key: 'businessTypeByBusinessTypeId', name: 'businessTypeByBusinessTypeId.businessTypeId', logicGate: 'AND', bracket: '', value: this.BUSINESS_TYPE_ATOMIC_GROUP_ID}
      ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: field.value,
        logicGate: field.logicGate,
        bracket: field.bracket || ''
      };
    });

    this.policyholderGroupService.getListOfPolicyholderGroups(pagedRequestDTO)
      .pipe(
        take(1),
        debounceTime(this.debounceTime),
      )
      .subscribe({
        next: (response) => {
          this.filteredEmployerGroups = response.resultList;
        },
        error: (error) => {
          this.openErrorDialog(this.displayStrings.bulkApplicationErrorMessagesList.fetchEmployerGroupError);
        }
      });
  }

  searchBrokers(searchValue: string): void {
    const pagedRequestDTO: PagedRequestDTO = {
      entityClass: 'TblBroker',
      pageSize: 10,
      pageNumber: 0,
      sortField: 'name',
      sortDirection: 'ASC',
      fieldMap: {}
    };

    const fields = [
      {key: 'name', name: 'name', logicGate: 'AND', bracket: '('},
      {key: 'brokerNumber', name: 'brokerNumber', logicGate: 'OR', bracket: ')'}
    ];

    fields.forEach((field) => {
      pagedRequestDTO.fieldMap[field.key] = {
        name: field.name,
        value: searchValue,
        logicGate: field.logicGate,
        bracket: field.bracket || ''
      };
    });

    this.brokerService.getListOfBrokers(pagedRequestDTO)
      .pipe(
        take(1),
        debounceTime(this.debounceTime),
      )
      .subscribe({
        next: (response: PagedResponseDTO<BrokerDTO>) => {
          this.filteredBrokers = response.resultList;
          this.filteredBrokers$.next(response.resultList);
        },
        error: (error) => {
          this.openErrorDialog(this.displayStrings.bulkApplicationErrorMessagesList.fetchBrokerError);
        }
      });
  }

  openFileUploadConfirmationDialog($event): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: this.displayStrings.bulkApplicationMessagesList.uploadNewExcelFileConfirmationMessage
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.openFileExplorer($event);
      }
    });
  }

  deleteApplication(applicationRow: ApplicationRowDTO): void {
    const index = this.getMatchedApplicationIndex(applicationRow);
    if (index !== -1) {
      this.bulkApplications.applications.splice(index, 1);
      this.revalidateAndDisplayApplicationRows();
    }
  }

  onDeleteApplication($event: MouseEvent, applicationRow: ApplicationRowDTO): void {
    $event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `${this.displayStrings.bulkApplicationMessagesList.deleteApplicationConfirmationMessage} ${applicationRow.idNumber}`
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.deleteApplication(applicationRow);
        this.revalidateAndDisplayApplicationRows();
      }
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      width: '40vw',
      data: errorMessage
    });
  }

  /**
   * Load all reference data needed for the edit dialog
   */
  loadReferenceData(): void {
    const referenceRequests = {
      relationshipTypes: this.referenceService.getAllByFieldName(ReferenceLookupValue.RelationshipTypeCode),
      titles: this.referenceService.getAllByFieldName(ReferenceLookupValue.Title),
      idTypes: this.referenceService.getAllByFieldName(ReferenceLookupValue.IdTypeCode),
      genders: this.referenceService.getAllByFieldName(ReferenceLookupValue.Gender),
      maritalStatuses: this.referenceService.getAllByFieldName(ReferenceLookupValue.MaritalStatus),
      ethnicities: this.referenceService.getAllByFieldName(ReferenceLookupValue.Ethnicity),
      bankOptions: this.referenceService.getAllByFieldName(ReferenceLookupValue.BankCode),
      accountTypes: this.referenceService.getAllByFieldName(ReferenceLookupValue.BankAccountType),
      addressTypes: this.referenceService.getAllByFieldName(ReferenceLookupValue.AddressTypeCode),
      options: this.referenceService.getAllByFieldName(ReferenceLookupValue.BonCapProductCode),
      directPayingMemberGroups: this.referenceService.getAllByFieldName(ReferenceLookupValue.DirectPayingMember)
    };

    forkJoin(referenceRequests)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (results) => {
          this.referenceData = results;
        },
        error: (error) => {
          console.error('Error loading references:', error);
        }
      });
  }

  getApplicationRowErrorMessage(applicationRow: ApplicationRowDTO): string {
    const errors = applicationRow.validationErrors;

    if (!errors || errors.length === 0) {
      return '';
    }

    const firstErrorKey = errors[0] as GlobalValidationError;
    const firstErrorMessage =
      DisplayStrings.bulkApplicationRowWarningTooltipMessagesList[firstErrorKey] || '';

    if (errors.length === 1) {
      return firstErrorMessage;
    }

    const additionalCount = errors.length - 1;
    return `${firstErrorMessage} + ${additionalCount} more`;
  }

  hasValidationErrors(applicationRow: ApplicationRowDTO): boolean {
    return applicationRow?.validationErrors && applicationRow.validationErrors.length > 0;
  }

  onEditApplication($event: MouseEvent, applicationRow: ApplicationRowDTO): void {
    $event.stopPropagation();

    const dialogRef = this.dialog.open(ObdBulkApplicationRowEditComponent, {
      width: '60vw',
      height: '85vh',
      data: {
        applicationRow,
        referenceData: this.referenceData,
        isDirectPayingMember: this.isDirectPayingMember() // Pass the flag
      }
    });

    dialogRef.afterClosed().subscribe((updatedApplication: ApplicationRowDTO) => {
      if (updatedApplication) {
        const index = this.getMatchedApplicationIndex(applicationRow);
        if (index !== -1) {
          this.bulkApplications.applications[index] = updatedApplication;
        }
        this.revalidateAndDisplayApplicationRows();
      }
    });
  }

  isDirectPayingMember(): boolean {
    if (this.referenceData?.directPayingMemberGroups?.length === 0) {
      return false;
    }
    return ApplicationUtil.isInEmployerGroupList(
      this.referenceData?.directPayingMemberGroups, this.selectedEmployerGroup?.policyholderGroupNumber
    );
  }

  onClickSubmitProcessing(): void {
    if (!this.bulkApplications?.applications || this.bulkApplications.applications.length === 0) {
      this.sharedService.openSnackBar('There are no applications to process');
      return;
    }
    const invalidApplications = this.bulkApplications.applications.filter(app =>
      app.validationErrors && app.validationErrors.length > 0
    );

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: invalidApplications.length > 0
        ? `Are you sure? There are ${invalidApplications.length} invalid rows that will not be processed.`
        : 'Are you sure you want to process these applications?',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const validApplications = this.bulkApplications.applications.filter(app =>
          !app.validationErrors || app.validationErrors.length === 0
        );

        const bulkPayload = {
          ...this.bulkApplications,
          applications: validApplications
        };

        this.onboardingService.bulkImportApplications(bulkPayload)
          .pipe(take(1))
          .subscribe({
            next: () => {
              const batchReferenceNumber = bulkPayload.applications[0]?.batchReferenceNumber;
              this.sharedService.openSnackBar(`The applications are busy being processed with reference number ${batchReferenceNumber} - check the applications tab  to see the status`);
              const route = this.getCustomRoute();
              if (route) {
                this.router.navigate([route]);
              }
              else {
                this.router.navigate(['/enterprise-management'], {queryParams: {tab: EnterpriseRoutesQueryParam.Applications}});
              }
            },
            error: (error) => {
              this.openErrorDialog(error?.businessMessage || 'Failed to process applications. Please try again.');
            }
          });
      }
    });
  }

  applyFilter(): void {
    if (!this.bulkApplications?.applications) {
      return;
    }
    switch (this.applicationFilter) {
    case ApplicationFilters.Valid:
      this.dataSource.data = this.bulkApplications.applications.filter(app => !this.hasValidationErrors(app));
      break;
    case ApplicationFilters.Invalid:
      this.dataSource.data = this.bulkApplications.applications.filter(app => this.hasValidationErrors(app));
      break;
    case ApplicationFilters.All:
    default:
      this.dataSource.data = this.bulkApplications.applications;
      break;
    }
  }

  private getMatchedApplicationIndex(applicationRow: ApplicationRowDTO): number {
    return this.bulkApplications.applications.findIndex(item =>
      this.areApplicationsEqual(item, applicationRow)
    );
  }

  private areApplicationsEqual(a: ApplicationRowDTO, b: ApplicationRowDTO): boolean {
    return Object.keys(a).every(key => {
      return a[key] === b[key];
    }) && Object.keys(b).every(key => {
      return a[key] === b[key];
    });
  }

  private getCustomRoute(): string {
    const portal = this.currentPortal || PortalType.Normal;
    const routes = {
      [PortalType.Normal]: null,
      [PortalType.OnBoarding]: buildRoute(portal, PathSegments.Application),
      [PortalType.Broker]: buildRoute(portal, PathSegments.Application)
    };
    return routes[portal] || null;
  }

  private handlePortalBasedCustomizations(portal: PortalType) {
    if (!portal) {
      return;
    }

    if (portal === PortalType.Broker) {
      // Always disable broker field for broker portal, regardless of auto-selection
      this.generalDetailsForm.get(Controls.Broker)?.disable();
      this.autoSelectBrokerForBrokerPortal();
    }
  }

  private autoSelectBrokerForBrokerPortal(): void {
    const profileLinkedEntityCode = this.sharedService.getProfileLinkedEntityCode();
    if (!profileLinkedEntityCode) {
      return;
    }

    this.searchBrokers(profileLinkedEntityCode);
    this.filteredBrokers$
      .pipe(take(1))
      .subscribe(brokers => {
        const matchingBroker: BrokerDTO = brokers.find(b => b.brokerNumber.toLowerCase() === profileLinkedEntityCode.trim().toLowerCase());
        if (matchingBroker) {
          this.selectedBroker = matchingBroker;
          const display = this.getDisplayString(matchingBroker.brokerNumber, matchingBroker.name);
          this.generalDetailsForm.get(Controls.Broker)?.setValue(display);
          this.setBrokerCodeAndEmployerGroupToAllData();
        }
      });
  }

  private getLoggedInUserName(): void {
    this.keycloakService
      .loadUserProfile()
      .then(profile => {
        this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;
      });
  }

  private getBatchReferenceNumber(brokerCode: string, employerGroupCode: string, loggedInUserName: string): string {
    return brokerCode + '_' + employerGroupCode + '_' + this.getUserTag(loggedInUserName) + '_' + this.getFormattedCurrentDate();
  }

  private getUserTag(loggedInUserName: string): string {
    if (typeof loggedInUserName !== 'string' || !loggedInUserName.trim()) {
      return '';
    }

    const parts = loggedInUserName.trim().split(/\s+/);

    if (parts.length === 1) {
      return parts[0][0].toUpperCase();
    }

    const firstInitial = parts[0][0]?.toUpperCase() || '';
    const lastInitial = parts[1][0]?.toUpperCase() || '';

    return firstInitial + lastInitial;
  }

  private getFormattedCurrentDate(): string {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${year}${month}${day}_${hours}${minutes}`;
  }

  private parseDisplayString(displayString: string): { code?: string; name?: string } {
    if (!displayString || displayString === 'N/A') {
      return {code: undefined, name: undefined};
    }

    const [code, ...nameParts] = displayString.split(': ');
    const name = nameParts.join(': ');

    return {code: code || undefined, name: name || undefined};
  }

  private isValidRow(row: any[]): boolean {
    const hasAppRef = row[ApplicationRowIndex.ApplicationReferenceNumber]?.toString().trim();
    const hasIdType = row[ApplicationRowIndex.IdType]?.toString().trim();
    const hasIdNumber = row[ApplicationRowIndex.IdNumber]?.toString().trim();
    return !!(hasAppRef && hasIdType && hasIdNumber);
  }

  private setBrokerCodeAndEmployerGroupToAllData(): void {
    if (this.bulkApplications?.applications && this.bulkApplications?.applications.length > 0) {
      const brokerCode = this.selectedBroker.brokerNumber.toString();
      const employerGroupCode = this.selectedEmployerGroup.policyholderGroupNumber.toString();
      this.bulkApplications.applications.forEach(application => {
        application.brokerCode = brokerCode;
        application.employerGroupCode = employerGroupCode;
        application.batchReferenceNumber = this.getBatchReferenceNumber(brokerCode, employerGroupCode, this.loggedInUserName);
      });
      this.bulkApplications.applications = [...this.bulkApplications.applications];
      this.dataSource = new MatTableDataSource(this.bulkApplications.applications);
    }
  }

  private safeFormatDate(format: string, value: any): string {
    try {
      if (typeof value === 'number' && value > 0) {
        return XLSX.SSF.format(format, value);
      }

      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return value;
      }
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const excelSerial = (date.getTime() - excelEpoch.getTime()) / (1000 * 60 * 60 * 24);

      return XLSX.SSF.format(format, excelSerial);
    } catch {
      return value;
    }
  }

  truncateToTwoDecimals(value: any): string {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return Math.floor(num * 100) / 100 + '';
  }

  private sanitiseInputString(value: any): string {
    if (!value) {
      return null;
    }
    return String(value).trim();
  }

  private sanitiseInputStringAndRemoveWhitespace(value: any): string | null {
    const sanitised = this.sanitiseInputString(value);
    return sanitised?.replace(/\s+/g, '') ?? null;
  }

  private sanitiseEmailString(value: any): string | null {
    const sanitised = this.sanitiseInputString(value);
    return sanitised?.toLowerCase() ?? null;
  }

  private generateStartDateOptions(): void {
    this.startDateOptions = ApplicationUtil.generateStartDateOptions();
    this.setDefaultStartDate();
  }

  private formatDate(date: Date, dateFormat: string = this.dateFormat): string {
    if (date) {
      return this.sharedService.formatDate(date, dateFormat);
    }
    return '';
  }

  private setDefaultStartDate(): void {
    const currentMonthOption = this.startDateOptions.find(option => option.isCurrentMonth);
    if (currentMonthOption) {
      this.generalDetailsForm.patchValue({[Controls.MembershipStartDate]: currentMonthOption.value});
    }
  }

  private updateAllApplicationsStartDate(newDate: string): void {
    if (this.bulkApplications?.applications?.length > 0) {
      this.bulkApplications.applications = this.bulkApplications.applications.map(app => ({
        ...app,
        membershipStartDate: newDate
      }));

      if (this.dataSource) {
        this.dataSource.data = [...this.bulkApplications.applications];
      }
    }
  }

}

